import type {
  RegisterMembershipScheme,
  RegisterYumenaviMembershipScheme,
  ContactFormScheme,
} from '@/features/user/constants/schemas';
import { AccountType } from '~/features/user/api/types';
import { CorporateAccountInfoSchemas } from '~/features/user/constants/corporateSchemas';

type UserCredential = {
  email?: string;
  password?: string;
  code?: string;
};

type Reason = {
  id: number;
  content: string;
};

export const useUserState = () => {
  /**
   * ユーザーの認証情報
   * NOTE: cognitoにコード認証するとき・会員情報変更の前に必要なため保存しておく
   * 実装によっては不要になるかもしれない
   */
  const userCredentials = useState<UserCredential | undefined>(
    'userCredentials',
    () => undefined,
  );

  /**
   * 現在編集中の会員情報
   */
  const editingMembership = useState<RegisterMembershipScheme | undefined>(
    'editingMembership',
    () => undefined,
  );

  /**
   * 現在編集中の夢なび会員情報
   */
  const editingYumenaviMembership = useState<
    RegisterYumenaviMembershipScheme | undefined
  >('editingYumenaviMembership', () => undefined);

  /**
   * 現在編集中の法人情報
   */
  const editingCorporateInfo = useState<
    CorporateAccountInfoSchemas | undefined
  >('editingCorporateInfo', () => undefined);

  /**
   * 現在編集中のお問い合わせ情報
   */
  const editingContactForm = useState<ContactFormScheme | undefined>(
    'editingContactForm',
    () => undefined,
  );

  // 選択された退会理由
  const selectedUnsubscribeReason = useState<Reason | undefined>(
    'selectedUnsubscribeReason',
    () => undefined,
  );

  /**
   * ログイン中のアカウント区分
   */
  const accountType = useState<AccountType | undefined>(
    'selectedAccountType',
() => undefined,
  );

  const setUserCredentials = (_credential: UserCredential | undefined) => {
    userCredentials.value = _credential;
  };

  const setEditingMembership = (
    _membership: RegisterMembershipScheme | undefined,
  ) => {
    editingMembership.value = _membership
      ? {
          lastName: _membership.lastName?.trim(),
          firstName: _membership.firstName?.trim(),
          lastNameKana: _membership.lastNameKana?.trim(),
          firstNameKana: _membership.firstNameKana?.trim(),
          phoneNumber: _membership.phoneNumber?.map((v) => v.trim()) || [],
          zipCode: _membership.zipCode?.trim(),
          address: _membership.address?.trim(),
        }
      : undefined;
  };

  const setEditingYumenaviMembership = (
    _membership: RegisterYumenaviMembershipScheme | undefined,
  ) => {
    editingYumenaviMembership.value = _membership
      ? {
          lastName: _membership.lastName?.trim(),
          firstName: _membership.firstName?.trim(),
          lastNameKana: _membership.lastNameKana?.trim(),
          firstNameKana: _membership.firstNameKana?.trim(),
          phoneNumber: _membership.phoneNumber?.map((v) => v.trim()) || [],
          birthYear: _membership.birthYear,
          birthMonth: _membership.birthMonth,
          birthDay: _membership.birthDay,
          gender: _membership.gender,
          zipCode: _membership.zipCode?.trim(),
          address1: _membership.address1?.trim(),
          address2: _membership.address2?.trim(),
          address3: _membership.address3?.trim(),
          address4: _membership.address4?.trim(),
        }
      : undefined;
  };

  const setEditingContactForm = (_contact: ContactFormScheme | undefined) => {
    editingContactForm.value = _contact
      ? {
          lastName: _contact.lastName?.trim(),
          firstName: _contact.firstName?.trim(),
          lastNameKana: _contact.lastNameKana?.trim(),
          firstNameKana: _contact.firstNameKana?.trim(),
          birthYear: _contact.birthYear,
          birthMonth: _contact.birthMonth,
          birthDay: _contact.birthDay,
          email: _contact.email?.trim(),
          phoneNumber: _contact.phoneNumber?.map((v) => v.trim()) || [],
          inquiryType: _contact.inquiryType,
          inquiryContent: _contact.inquiryContent?.trim(),
        }
      : undefined;
  };

  /**
   * 編集中の法人情報を設定
   * @param _corporateInfo 法人情報
   */
  const setEditingCorporateInfo = (
    _corporateInfo: CorporateAccountInfoSchemas | undefined,
  ) => {
    editingCorporateInfo.value = _corporateInfo
      ? {
          // 法人種別
          corporateType: _corporateInfo.corporateType,
          // 法人種別前後区分
          corporateTypePosition: _corporateInfo.corporateTypePosition,
          // 法人名
          corporateName: _corporateInfo.corporateName?.trim(),
          // 法人名（カナ）
          corporateNameKana: _corporateInfo.corporateNameKana?.trim(),
          // 本社郵便番号
          headOfficePostalCode: _corporateInfo.headOfficePostalCode?.trim(),
          // 本社所在地
          headOfficeAddress: _corporateInfo.headOfficeAddress?.trim(),
          // 本社所在地詳細
          headOfficeAddressDetail:
            _corporateInfo.headOfficeAddressDetail?.trim(),
          // アカウント管理部署名
          departmentName: _corporateInfo.departmentName?.trim(),
          // 管理部署郵便番号
          departmentPostalCode: _corporateInfo.departmentPostalCode?.trim(),
          // 管理部署所在地
          departmentAddress: _corporateInfo.departmentAddress?.trim(),
          // 管理部署所在地詳細
          departmentAddressDetail:
            _corporateInfo.departmentAddressDetail?.trim(),
          // 管理部署メールアドレス
          departmentEmail: _corporateInfo.departmentEmail?.trim(),
          // 管理部署電話番号
          departmentPhone: _corporateInfo.departmentPhone?.map((v) =>
            v.trim(),
          ) || ['', '', ''],
        }
      : undefined;
  };

  /** 退会理由を更新 */
  const setUnsubscribeReason = (reason: Reason | undefined) => {
    selectedUnsubscribeReason.value = reason;
  };

  /**
   * アカウント区分を設定（リセット不可能）
   * @param _accountType 設定するアカウント区分
   */
  const setAccountType = (_accountType: AccountType) => {
    accountType.value = _accountType;
  };

  const resetAll = () => {
    setUserCredentials(undefined);
    setEditingMembership(undefined);
    setEditingYumenaviMembership(undefined);
    setEditingContactForm(undefined);
    setEditingCorporateInfo(undefined);
    setUnsubscribeReason(undefined);
  };

  return {
    userCredentials: readonly(userCredentials),
    editingMembership: readonly(editingMembership),
    editingYumenaviMembership: readonly(editingYumenaviMembership),
    editingCorporateInfo: readonly(editingCorporateInfo),
    editingContactForm: readonly(editingContactForm),
    selectedUnsubscribeReason: readonly(selectedUnsubscribeReason),
    accountType: readonly(accountType),
    setUserCredentials,
    setEditingMembership,
    setEditingYumenaviMembership,
    setEditingCorporateInfo,
    setEditingContactForm,
    setUnsubscribeReason,
    setAccountType,
    resetAll,
  };
};
